/** Carte d'une mesure du catalogue : coût/rendement, montée en charge, pilules d'effets, « Effet sur 5 ans », hypothèse. */
import type { Measure, Profile } from "../../../engine/src/types.js";
import { STATUS_LABELS, type BreakdownItem, type UiMeasure } from "../content-types.js";
import { variantCost, type PriceEffects, type TrajectoryPoint } from "../engine-adapter.js";
import { costAsBalance, escapeHtml as e, int, missionShort, num, signed } from "../format.js";
import type { Store } from "../state.js";
import { hasPriceEffect, priceLine } from "./price-effects.js";
import { fcBadge } from "../factcheck.js";
import { srcButton } from "./source-info.js";
import { measurePol, polPill } from "../political.js";

const lastOf = (v: Profile) => (Array.isArray(v) ? (v[v.length - 1] ?? 0) : v);
const firstOf = (m: Measure) => (Array.isArray(m.cost) ? (m.cost[0] ?? 0) : m.ramp > 1 ? m.cost / m.ramp : m.cost);
const dated = (v: Profile) => (Array.isArray(v) ? ` à N+${v.length}` : "");
/** Granularité du curseur d'hypothèse (ergonomie, pas une valeur métier) : ~10 à 70 crans selon l'amplitude. */
const sliderStep = (span: number) => (span >= 50 ? 5 : span >= 10 ? 1 : span >= 1 ? 0.1 : 0.01);

export function fxPills(store: Store, m: Measure, regimeBn?: number, variantId?: string): string {
  const f = m.fx;
  const P: string[] = [];
  const add = (cls: string, txt: string) => P.push(`<span class="pill ${cls}">${txt}</span>`);
  if (f.g !== undefined) add(lastOf(f.g) > 0 ? "ok" : "bad", `croissance ${signed(lastOf(f.g), 2)} pt${dated(f.g)}`);
  if (f.trade !== undefined) add(lastOf(f.trade) > 0 ? "ok" : "bad", `commerce ext. ${signed(lastOf(f.trade), 0)} Md€${dated(f.trade)}`);
  if (f.lab !== undefined) add(lastOf(f.lab) < 0 ? "bad" : "ok", `pop. active ${signed(lastOf(f.lab), 2)} %/an`);
  if (f.u !== undefined) add(lastOf(f.u) > 0 ? "bad" : "ok", `chômage ${signed(lastOf(f.u), 2)} pt${dated(f.u)}`);
  if (f.pov !== undefined) add(lastOf(f.pov) > 0 ? "bad" : "ok", `pauvreté ${signed(lastOf(f.pov), 2)} pt`);
  if (f.coh !== undefined) add(lastOf(f.coh) > 0 ? "ok" : "bad", `cohésion ${signed(lastOf(f.coh), 0)}`);
  if (f.conf !== undefined) add(lastOf(f.conf) > 0 ? "ok" : "bad", `confiance ${signed(lastOf(f.conf), 0)}`);
  if (f.eu !== undefined) add(lastOf(f.eu) > 0 ? "ok" : "bad", `UE ${signed(lastOf(f.eu), 0)}`);
  if (f.co2 !== undefined) add(lastOf(f.co2) < 0 ? "ok" : "bad", `CO₂ ${signed(lastOf(f.co2), 1)} %/an`);
  if (f.svc) {
    for (const [id, v] of Object.entries(f.svc)) {
      const label = missionShort(store.content.baseline.state.missions.find((x) => x.id === id)?.label ?? id);
      add(lastOf(v) > 0 ? "ok" : "bad", `${e(label)} ${signed(lastOf(v), 2)}`);
    }
  }
  if (f.asso !== undefined) add(lastOf(f.asso) < 0 ? "bad" : "ok", `Sécu ${signed(lastOf(f.asso), 0)} Md€`);
  if (f.apul !== undefined) add(lastOf(f.apul) < 0 ? "bad" : "ok", `collectivités ${signed(lastOf(f.apul), 0)} Md€`);
  // Coût politique : même unité et même formule d'affichage que leviers, économies et réponses de crise (ADR 0011).
  P.push(polPill(measurePol(store.ctx.params, m, regimeBn ?? lastOf(m.cost), variantId), { ratio: true }));
  return P.join("");
}

export function trajectoryTable(points: TrajectoryPoint[]): string {
  const row = (label: string, pick: (p: TrajectoryPoint) => number, dec: number, invert: boolean) =>
    `<tr><td>${label}</td>${points
      .map((p) => {
        const v = pick(p);
        const eps = Math.pow(10, -dec) / 2;
        const good = invert ? v < -eps : v > eps;
        const bad = invert ? v > eps : v < -eps;
        return `<td class="${good ? "up" : bad ? "down" : "flat"}">${Math.abs(v) < eps ? "·" : signed(v, dec)}</td>`;
      })
      .join("")}</tr>`;
  return `<table class="traj"><tr><th></th>${points.map((_, i) => `<th>N+${i + 1}</th>`).join("")}</tr>
    ${row("Solde public (Md€)", (p) => p.d_balance_apu_bn, 0, false)}
    ${row("Croissance (pt)", (p) => p.d_growth_pt, 2, false)}
    ${row("Chômage (pt)", (p) => p.d_unemployment_pt, 2, true)}
    ${row("Commerce ext. (Md€)", (p) => p.d_trade_bn, 0, false)}
    ${row("Dette / PIB (pt)", (p) => p.d_debt_pct_gdp, 1, true)}
  </table><p class="hint">Mesure seule, financée par la dette, hors crises, comparée au budget tendanciel. Vert = favorable.</p>`;
}

/** Tableau « Concrètement » : poste, quantité, coût unitaire, total, délai (PRD §14.4). */
export function breakdownTable(items: BreakdownItem[], complete: boolean | undefined, title: string, measureId?: string): string {
  if (!items.length) return "";
  const rows = items
    .map((b) => {
      const qty = b.quantity !== undefined ? `${int(b.quantity)}${b.unit ? ` ${e(b.unit)}` : ""}` : "—";
      const uc = b.unit_cost_eur !== undefined ? `${int(b.unit_cost_eur)} €` : "—";
      const tot = b.total_bn !== undefined ? `${num(b.total_bn, 2)} Md€` : "—";
      const delay = b.delay_years !== undefined ? `${num(b.delay_years, 0)} an${b.delay_years > 1 ? "s" : ""}` : "—";
      const src = srcButton({ title: `${title} · ${b.item}`, sources: [b.source], notes: b.note ? [b.note] : [], figureId: measureId !== undefined ? `measures/${measureId}/breakdown/${items.indexOf(b)}/${b.unit_cost_eur !== undefined ? "unit_cost_eur" : "total_bn"}` : undefined }, { inline: true });
      // data-label : en-tête répété par cellule pour l'affichage en fiches sous 520 px (styles.css, v0.4) ; « — » masqué en fiche.
      const cell = (label: string, v: string) => `<td data-label="${label}"${v === "—" ? ' class="na"' : ""}>${v}</td>`;
      return `<tr><td>${e(b.item)}${src}</td>${cell("Quantité", qty)}${cell("Coût unitaire", uc)}${cell("Total /an", tot)}${cell("Délai", delay)}</tr>`;
    })
    .join("");
  const sum = items.every((b) => b.total_bn !== undefined) ? items.reduce((a, b) => a + (b.total_bn ?? 0), 0) : undefined;
  return `<p class="xs">${complete ? `<span class="badge ok">décomposition complète</span> la somme des postes fait le coût de la mesure.` : `<span class="badge warn">décomposition illustrative</span> les postes ne couvrent pas tout le coût affiché.`}</p>
  <div class="tblwrap"><table class="bd"><tr><th>Poste</th><th>Quantité</th><th>Coût unitaire</th><th>Total /an</th><th>Délai</th></tr>${rows}${sum !== undefined ? `<tr class="lt"><td>Total des postes</td><td></td><td></td><td data-label="Total /an">${num(sum, 2)} Md€</td><td></td></tr>` : ""}</table></div>`;
}

/** Décomposition d'une variante : quantités et totaux du catalogue × `scale` (coûts unitaires inchangés). */
function scaledBreakdown(items: BreakdownItem[], scale: number): BreakdownItem[] {
  return items.map((b) => ({
    ...b,
    ...(b.quantity !== undefined ? { quantity: b.quantity * scale } : {}),
    ...(b.total_bn !== undefined ? { total_bn: b.total_bn * scale } : {}),
  }));
}

/** Contrôle segmenté des variantes : « catalogue » + variantes chiffrables. Désactivé si le moteur ne les prend pas. */
function variantsControl(store: Store, m: UiMeasure, regimeCatalogue: number): string {
  const vs = (m.variants ?? []).filter((v) => variantCost(m, v.id) !== undefined);
  if (!vs.length) return "";
  const cur = store.state.programme.variants?.[m.id] ?? "";
  const off = !store.engine.caps.variants;
  const btn = (id: string, label: string, cost: number) =>
    `<button type="button" class="segb" data-vm="${m.id}" data-variant="${e(id)}" aria-pressed="${cur === id}" ${off ? "disabled" : ""}>${e(label)}<small>${costAsBalance(cost)} Md€</small></button>`;
  return `<div class="seg" role="group" aria-label="Variante de la mesure">${btn("", "Catalogue", regimeCatalogue)}${vs.map((v) => btn(v.id, v.label, variantCost(m, v.id)!)).join("")}</div>
  ${off ? `<p class="hint warn">Variantes affichées pour information : le moteur ne les chiffre pas encore (bientôt).</p>` : `<p class="hint">Choisir une variante coche la mesure et remplace son coût dans la simulation.</p>`}`;
}

/** Effets prix d'une mesure seule (mémorisés par mesure + hypothèse + variante). */
const priceCache = new Map<string, PriceEffects | null>();
export function measurePriceEffects(store: Store, m: UiMeasure): PriceEffects | null {
  if (!store.engine.caps.priceEffects) return null;
  const hyp = store.state.programme.hyp[m.id];
  const v = store.state.programme.variants?.[m.id] ?? "";
  const key = `${m.id}|${hyp ?? ""}|${v}`;
  if (!priceCache.has(key)) {
    let pe: PriceEffects | null = null;
    try {
      pe = store.engine.priceEffects(store.ctx, store.prg({ measure_ids: [m.id], hyp: hyp !== undefined ? { [m.id]: hyp } : {}, ...(v ? { variants: { [m.id]: v } } : {}) }), { revenue_raises: {}, across_the_board_cut: 0 });
    } catch {
      pe = null;
    }
    priceCache.set(key, pe);
  }
  return priceCache.get(key) ?? null;
}

/** Statut de fact-check de la fourchette de coût, en note du panneau source. */
function rangeBadgeNote(id: string): string[] {
  const b = fcBadge(`measures/${id}/cost_range`);
  return b ? [`Fourchette de coût — fact-check : ${b.label}${b.comment ? ` (${b.comment})` : ""}.`] : [];
}

export function measureCard(store: Store, m: UiMeasure): string {
  const st = store.state;
  const on = st.programme.measure_ids.includes(m.id);
  const excluded = m.exclusive_with.some((x) => st.programme.measure_ids.includes(x));
  const hypVal = st.programme.hyp[m.id] ?? m.hyp?.default;
  const variant = st.programme.variants?.[m.id];
  const hypObj = hypVal !== undefined ? { [m.id]: hypVal } : {};
  const regimeCatalogue = store.engine.needGap(store.ctx, { measure_ids: [m.id], hyp: hypObj });
  const regime = variant ? store.engine.needGap(store.ctx, store.prg({ measure_ids: [m.id], hyp: hypObj, variants: { [m.id]: variant } })) : regimeCatalogue;
  const first = firstOf(m) * (m.cost !== 0 ? regime / lastOf(m.cost) : 1);
  const badgeCls = m.status === "sourced" ? "ok" : m.status === "estimé_ia" ? "ai" : "";
  const notes: string[] = [];
  if (m.cost_low !== undefined && m.cost_high !== undefined) notes.push(`Fourchette de coût : ${num(lastOf(m.cost_low))} – ${num(lastOf(m.cost_high))} Md€/an.`);
  if (m.note) notes.push(m.note);
  // Badge de fact-check : celui du coût (`measures/<id>/cost`) ; la fourchette dans le panneau (ADR 0012 « contrat UI »).
  const src = srcButton({ title: m.label, sources: m.sources.length ? m.sources : ["TODO"], status: m.status, notes: [...notes, ...rangeBadgeNote(m.id)], derivation: m.derivation, figureId: `measures/${m.id}/cost` }, { inline: true });
  const derivedBadge = m.derivation === "scaled" ? ` <span class="badge warn" title="Chiffre dérivé de la source par règle de trois ou contrefactuel">dérivé</span>` : "";
  const mandateYears = store.content.params.mandate_years?.value ?? store.content.baseline.asso.trend.length;
  const excl = m.exclusive_with.length
    ? `<div class="hint">Incompatible avec : ${m.exclusive_with.map((x) => e(store.content.measures.find((y) => y.id === x)?.label ?? x)).join(", ")}.</div>`
    : "";
  const hyp = m.hyp
    ? `<div class="hyp" ${on ? "" : "hidden"}><label for="hyp-${m.id}">${e(m.hyp.label)} : <b id="vh-${m.id}">${num(hypVal ?? m.hyp.default, 1)}</b></label>
       <input id="hyp-${m.id}" type="range" min="${m.hyp.low}" max="${m.hyp.high}" step="${sliderStep(m.hyp.high - m.hyp.low)}" value="${hypVal ?? m.hyp.default}" data-hyp="${m.id}" aria-valuetext="${num(hypVal ?? m.hyp.default, 1)}">
       <div class="hint">${m.hyp.range === "published" ? `Bornes du curseur : fourchette des chiffrages publiés (${num(m.hyp.low, 0)} – ${num(m.hyp.high, 0)}), voir la source.` : m.hyp.range === "estimated" ? "Bornes du curseur : proposées par l'estimation IA (non sourcées)." : "Bornes du curseur : convention de modélisation (non sourcée)."} Hypothèse contestée : déplace le curseur pour voir comment le chiffrage change.</div></div>`
    : "";
  const vObj = variant ? m.variants?.find((x) => x.id === variant) : undefined;
  const bdItems = vObj ? scaledBreakdown(m.breakdown ?? [], vObj.scale) : (m.breakdown ?? []);
  const concretely =
    bdItems.length || m.variants?.length
      ? `<details class="more concr" data-concr="${m.id}"><summary>Concrètement</summary>${variantsControl(store, m, regimeCatalogue)}${breakdownTable(bdItems, m.breakdown_complete, m.label, vObj ? undefined : m.id)}${vObj ? `<p class="hint">Postes mis à l'échelle de la variante (× ${num(vObj.scale, 2)}).${vObj.note ? ` ${e(vObj.note)}` : ""}</p>` : ""}</details>`
      : "";
  const fiscal = m.category === "fiscalite_menages" || m.category === "fiscalite_entreprises";
  const pe = fiscal ? measurePriceEffects(store, m) : null;
  const price = pe && hasPriceEffect(pe) ? priceLine(store, pe, { compact: true }) : "";
  return `<div class="m" data-measure="${m.id}">
    <input type="checkbox" id="m-${m.id}" data-m="${m.id}" ${on ? "checked" : ""} ${excluded && !on ? "disabled" : ""} aria-label="${e(m.label)}">
    <div class="body">
      <div class="mh"><label class="t" for="m-${m.id}">${e(m.label)}</label><div class="cost">${costAsBalance(regime)}<small>Md€/an en régime${vObj ? `<br>variante : ${e(vObj.label)}` : ""}${Math.abs(first - regime) > 0.3 ? `<br>N+1 : ${costAsBalance(first)}` : ""}${m.ramp > 1 ? `<br>montée en charge ${m.ramp} ans` : ""}</small></div></div>
      <div class="mb"><span class="badge ${badgeCls}">${STATUS_LABELS[m.status]}</span>${derivedBadge}${src}</div>
      ${m.description ? `<div class="d">${e(m.description)}</div>` : ""}
      <div class="fx">${fxPills(store, m, regime, variant)}</div>
      ${price}
      <details class="more"><summary>Partisans / critiques</summary><div class="claims"><div><b>Partisans :</b> ${e(m.claims.supporters)}</div><div><b>Critiques :</b> ${e(m.claims.critics)}</div></div></details>
      <details class="more traj-d" data-traj="${m.id}"><summary>Effet sur ${mandateYears} ans</summary><div class="trajbox"><p class="hint">Calcul…</p></div></details>
      ${hyp}${excluded && !on ? `<div class="hint warn">Désactivée : incompatible avec une mesure déjà cochée.</div>` : excl}
    </div>
    ${concretely ? `<div class="full">${concretely}</div>` : ""}
  </div>`;
}
